import { TextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { createSpinner, showSpinner } from '@syncfusion/ej2-popups';
import { MessageComponent } from '@syncfusion/ej2-react-notifications';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { get } from "../../utils/Api";
import React, { useState, useEffect, useRef } from 'react';
import { storePurpose } from "../../utils/Purpose";
import { env } from '../../env'

function ConstituencySearch(props) {
    // Declare variables using const or let
    const { purposeId, formId, formSettings, provider, setValue, getValues, address } = props;
    const selectedConstituencyHtmlAttr = { name: "selectedConstituencyValue" };
    const selectedConstituencyFields = { text: 'name', value: 'id' };
    const dropdownRef = useRef(null);
    let spinnerInstance = null;

    // Declare state variables using useState
    const [isConstituencySearching, setIsConstituencySearching] = useState(false);
    const [isConstituencySelected, setIsConstituencySelected] = useState(false);
    const [selectedConstituency, setSelectedConstituency] = useState(getValues('constituency') || null);
    const [constituencyState, setConstituencyState] = useState({
        foundConstituencies: [],
        hasConstituencies: false,
        constituencyErrorMessage: '',
        constituencyLookupError: false,
    });

    // Declare event handlers or other functions
    const handleConstituencySelected = async (args) => {
        if (args !== null && args.value !== null && !isConstituencySearching) {

            let requiresUpdate = (!selectedConstituency || (selectedConstituency && selectedConstituency.memberId !== args.memberId));

            setSelectedConstituency(args);
            storePurpose(`df.${formId}.const`, args, purposeId);

            if (requiresUpdate === true) {
                setValue('constituencyChange', true);
            }
            else {
                setValue('constituencySelected', true);
            }

            setValue('constituency', args);
            setIsConstituencySelected(true);
        }
    };

    const ClearConstituency = () => {
        setIsConstituencySearching(false);
        setIsConstituencySelected(false);
        setSelectedConstituency(null);
        setValue('constituency', null);
        setValue('constituencySelected', false);
        setValue('constituencyChange', false);
        localStorage.removeItem(`df.${formId}.const`);
        setConstituencyState((prevFormData) => ({ ...prevFormData, foundConstituencies: [], hasConstituencies: false, constituencyErrorMessage: '', constituencyLookupError: false }));
    };

    const handleConstituencySearch = async () => {
        if (!isConstituencySearching && address && address.postcode !== '' && address.postcode.length >= 5) {
            if (selectedConstituency === null || (selectedConstituency !== null && (selectedConstituency.isMultiResult || address.postcode !== selectedConstituency.addressPostcode))) {
                if (!selectedConstituency?.isMultiResult) {
                    ClearConstituency();
                }

                setIsConstituencySearching(true);

                const response = await get(`${provider.url}/api/v1.0/parliament/search?cfk=${encodeURIComponent(provider.key)}&postcode=${address.postcode}`, false);
                if (response.status === 200) {
                    const constituencyList = response.data.map((constituency) => ({
                        id: constituency.constituencyId,
                        name: constituency.constituencyName,
                        memberId: constituency.memberId,
                        memberName: constituency.memberName,
                        memberThumbnailUrl: constituency.memberThumbnailUrl,
                        memberPartyName: constituency.memberPartyName,
                        memberPartyBackgroundColour: constituency.memberPartyBackgroundColour,
                        memberPartyForegroundColour: constituency.memberPartyForegroundColour,
                        memberEmail: constituency.memberEmail,
                        isMultiResult: response.data.length > 1,
                        addressPostcode: address.postcode
                    }));
                    setConstituencyState((prevFormData) => ({ ...prevFormData, foundConstituencies: constituencyList, hasConstituencies: constituencyList.length > 0, constituencyLookupError: constituencyList.length === 0, constituencyErrorMessage: "No constituencies found for postcode, please check postcode entered and try again." }));
                    if (constituencyList.length === 1) {
                        handleConstituencySelected(constituencyList[0]);
                    } else if (selectedConstituency !== null) {
                        let index = constituencyList.findIndex(member => member.memberId === selectedConstituency.memberId);
                        if (index !== -1) {
                            handleConstituencySelected(constituencyList[index]);
                            dropdownRef.current.index = index;
                        }
                    }

                    setIsConstituencySearching(false);
                } else {
                    ClearConstituency();
                    setConstituencyState((prevFormData) => ({ ...prevFormData, constituencyLookupError: true, hasConstituencies: false, constituencyErrorMessage: response.status === 410 ? "Currently there is no member representative for this constituency, please try again after by-election." : "Unable to search for constituency, please try again." }));
                }
            } else {
                // Already have searches
                setConstituencyState((prevFormData) => ({ ...prevFormData, foundConstituencies: constituencyState.foundConstituencies.length > 0 ? constituencyState.foundConstituencies : [selectedConstituency], hasConstituencies: true, constituencyLookupError: false }));
                setValue('constituencySelected', true);
                setIsConstituencySelected(true);
            }
        } else if (address && address.postcode !== '' && address.postcode.length < 5) {
            ClearConstituency();
            setConstituencyState((prevFormData) => ({ ...prevFormData, constituencyLookupError: true, hasConstituencies: false, constituencyErrorMessage: "Unable to search for constituency, postcode is too short, please update and try again." }));
        }
    };

    // Declare effects using useEffect
    useEffect(() => {
        if (env.VITE_ENV && env.VITE_ENV.toLowerCase() !== 'production') {
            console.log(`ConstituencySearch.useEffect.spinner :: isConstituencySearching: ${isConstituencySearching}`);
        }

        if (isConstituencySearching && spinnerInstance !== null) {
            createSpinner({
                // Specify the target for the spinner to show
                target: spinnerInstance,
            });
            showSpinner(spinnerInstance);
        }
    }, [isConstituencySearching, spinnerInstance])


    useEffect(() => {
        if (env.VITE_ENV && env.VITE_ENV.toLowerCase() !== 'production') {
            console.log(`ConstituencySearch.useEffect.address :: isSearching: ${isConstituencySearching} postcode:${address?.postcode}`);
        }

        handleConstituencySearch();

    }, [address]);

    function constituencyHeaderTemplate(data) {
        return (<div className="header"> <span>Portrait</span> <span className="info">Constituency Info</span></div>)
    }

    function constituencyItemTemplate(data) {

        const imageStyle = {
            margin: "6px 16px",
            float: "left",
            width: "50px",
            height: "50px",
            borderRadius: "50%",
            border: `3px solid #${data.memberPartyBackgroundColour}`,
        };

        return (<div>
            <img style={imageStyle} src={data.memberThumbnailUrl} alt="portrait" />
            <div style={memberNameStyle}>{data.memberName}</div>
            <div style={constituencyStyle}>{data.name}</div>
        </div>)
    }

    function constituencyValueTemplate(data) {

        const imageStyle = {
            borderRadius: "50%",
            float: "left",
            marginTop: "2px",
            marginLeft: "2px",
            border: `2px solid #${data.memberPartyBackgroundColour}`,
        };

        return (<div className="dropdown-width" style={{ width: '100%', height: '100%' }}>
            <img style={imageStyle} src={data.memberThumbnailUrl} alt="portrait" height="26" width="26" />
            <div style={constituencyNameStyle}>{data.memberName} ({data.name})</div>
        </div>)
    }

    // Declare Styles
    const memberNameStyle = {
        display: "block!important",
        opacity: ".87",
        fontSize: "16px",
        marginTop: "8px"
    }

    const memberImageStyle = {
        margin: "6px 16px",
        float: "left",
        width: "50px",
        height: "50px",
        borderRadius: "50%",
        border: `2px solid #${selectedConstituency?.memberPartyBackgroundColour}`,
    }

    const constituencyStyle = {
        opacity: ".54",
        fontSize: "14px",
        marginTop: "-15px",
        marginBottom: "7px"
    }

    const constituencyNameStyle = {
        padding: "5px 42px",
        opacity: ".87",
    }

    return (
        <div>
            <div className="container">
                <div className="row">
                    <div className="col-xs-12">
                        <b className="example-label">Constituency:</b>
                    </div>
                    <div className="col-xs-12 mt-2" style={!isConstituencySearching && constituencyState.constituencyLookupError ? {} : { display: 'none' }}>
                        <MessageComponent severity="Warning" variant="Filled" content={constituencyState.constituencyErrorMessage}></MessageComponent >
                    </div>

                    <div className="col-xs-12 mt-2" style={isConstituencySearching ? {} : { display: 'none' }}>
                        <div ref={spinner => {
                            spinnerInstance = spinner;
                        }} id="spinner" />
                    </div>

                    <div className="col-xs-12 col-sm-6 col-md-5 mt-3" style={constituencyState.foundConstituencies.length > 1 ? {} : { display: 'none' }}>
                        <DropDownListComponent
                            ref={dropdownRef}
                            id="ddlelement"
                            fields={selectedConstituencyFields}
                            valueTemplate={constituencyValueTemplate = constituencyValueTemplate.bind(this)}
                            itemTemplate={constituencyItemTemplate = constituencyItemTemplate.bind(this)}
                            floatLabelType="Auto"
                            dataSource={constituencyState.foundConstituencies}
                            placeholder="Select your member of parliament representative"
                            select={({ itemData }) => {
                                handleConstituencySelected(itemData, true);
                            }}
                            htmlAttributes={selectedConstituencyHtmlAttr}
                            value={selectedConstituency} >
                        </DropDownListComponent>
                    </div>

                    <div className="col-xs-12 col-sm-6 col-md-5 mt-2" style={constituencyState.foundConstituencies.length === 1 ? {} : { display: 'none' }}>
                        <div className="dropdown-width">
                            <img style={memberImageStyle} src={(selectedConstituency !== null ? selectedConstituency.memberThumbnailUrl : '')} alt="portrait" />
                            <div style={constituencyNameStyle}>{(selectedConstituency !== null ? selectedConstituency.memberName : '')}</div>
                            <div className="pt-2" style={constituencyStyle}>{(selectedConstituency !== null ? selectedConstituency.name : '')}</div>
                        </div>
                    </div>

                    <div className="col-xs-12 col-sm-6 col-md-4 mt-3" style={isConstituencySelected && selectedConstituency !== null && (formSettings.recipientToOverride === undefined || formSettings.recipientToOverride === false) && (formSettings.recipientTo === undefined || formSettings.recipientTo === null || formSettings.recipientTo === '') ? { maxWidth: '300px' } : { display: 'none' }}>
                        <TextBoxComponent type="text" readOnly={true} placeholder="Member Of Parliament Email" floatLabelType="Always" value={selectedConstituency !== null ? selectedConstituency.memberEmail : ''} />
                    </div>

                    <div className="col-xs-12 col-sm-6 col-md-4 mt-3" style={isConstituencySelected && selectedConstituency !== null && formSettings.recipientToOverride !== undefined && formSettings.recipientToOverride === false && formSettings.recipientTo !== undefined && formSettings.recipientTo !== null && formSettings.recipientTo !== '' ? { maxWidth: '300px' } : { display: 'none' }}>
                        <TextBoxComponent type="text" readOnly={true} placeholder="To Recipients" floatLabelType="Always" value={formSettings.recipientTo !== null && selectedConstituency !== null ? selectedConstituency.memberEmail + ', ' + formSettings.recipientTo : formSettings.recipientTo !== null && selectedConstituency?.memberEmail === null ? formSettings.recipientTo : ''} />
                    </div>

                    <div className="col-xs-12 col-sm-6 col-md-4 mt-3" style={isConstituencySelected && selectedConstituency !== null && formSettings.recipientToOverride !== undefined && formSettings.recipientToOverride === true && formSettings.recipientTo !== undefined && formSettings.recipientTo !== null ? { maxWidth: '300px' } : { display: 'none' }}>
                        <TextBoxComponent type="text" readOnly={true} placeholder="To Recipients" floatLabelType="Always" value={formSettings.recipientTo !== null ? formSettings.recipientTo : ''} />
                    </div>

                    <div className="col-xs-12 col-sm-6 col-md-3 mt-3" style={isConstituencySelected && selectedConstituency !== null && formSettings.recipientCopy !== null && formSettings.recipientCopy !== '' ? { maxWidth: '300px' } : { display: 'none' }}>
                        <TextBoxComponent type="text" readOnly={true} placeholder="Other Recipients" floatLabelType="Always" value={formSettings.recipientCopy} />
                    </div>
                </div>

            </div>
        </div>
    );
} export default ConstituencySearch;